import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import api from '../api';
import {
  IconDashboard, IconProjects, IconTasks, IconDocuments, IconContacts,
  IconCalendar, IconUsers, IconReports, IconWorkspace, IconImport, IconCompanies,
  IconPlatform, IconSettings, IconLogout, IconLock,
} from './Icons';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', Icon: IconDashboard },
  { label: 'Companies', path: '/companies',      Icon: IconCompanies },
  { label: 'Projects',  path: '/projects',  Icon: IconProjects },
  { label: 'Tasks',     path: '/tasks',     Icon: IconTasks },
  { label: 'Documents', path: '/documents', Icon: IconDocuments },
  { label: 'Contacts',  path: '/contacts',  Icon: IconContacts },
  { label: 'Calendar',  path: '/calendar',  Icon: IconCalendar },
  { label: 'Users',     path: '/users',     Icon: IconUsers },
  { label: 'Reports',   path: '/reports',   Icon: IconReports },
  { label: 'Workspace', path: '/workspace',      Icon: IconWorkspace },
  { label: 'Import',    path: '/import',         Icon: IconImport },
  { label: 'Platform',  path: '/platform-admin', Icon: IconPlatform },
  { label: 'Settings',  path: '/settings',  Icon: IconSettings },
];

const ADMIN_ROLES = ['gpf_administrator', 'gpf_system_admin', 'administrator'];

export default function Layout({ children }) {
  const { user, logout }  = useAuth();
  const { can, role }     = usePermissions();
  const navigate          = useNavigate();
  const location          = useLocation();
  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [isMobile,    setIsMobile]    = useState(window.innerWidth <= 768);
  const [workspace,      setWorkspace]      = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [planFeatures,   setPlanFeatures]   = useState(() => {
    // Use cached version instantly — eliminates flash on repeat visits
    try {
      const cached = localStorage.getItem('gpf_plan_features');
      if (cached) return JSON.parse(cached);
    } catch {}
    return {
      free:     { documents: false, calendar: false, reports: false, ai_agent: false, permissions: false },
      team:     { documents: true,  calendar: true,  reports: true,  ai_agent: false, permissions: false },
      business: { documents: true,  calendar: true,  reports: true,  ai_agent: true,  permissions: true  },
    };
  });
  const [planLoaded,     setPlanLoaded]     = useState(() => !!localStorage.getItem('gpf_plan_features'));
  const [userPlan,       setUserPlan]       = useState('free');
  const [showLockModal,  setShowLockModal]  = useState(false);
  const [lockedFeature,  setLockedFeature]  = useState('');

  useEffect(() => {
    api.get('/gpf/v1/workspace')
      .then(res => {
        setWorkspace(res.data);
        localStorage.setItem('gpf_workspace_cache', JSON.stringify(res.data));
        const plan = res.data.plan || 'free';
        setUserPlan(plan);
        // Load plan feature limits
        return api.get('/gpf/v1/admin/plan-limits');
      })
      .then(res => {
        if (res?.data) {
          setPlanFeatures(res.data);
          localStorage.setItem('gpf_plan_features', JSON.stringify(res.data));
        }
        setPlanLoaded(true);
      })
      .catch(() => {});

    // Check platform admin status server-side
    api.get('/wp/v2/users/me?context=edit')
      .then(res => {
        const roles = res.data.roles || [];
        setIsPlatformAdmin(roles.includes('administrator'));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const FEATURE_MAP = {
    '/documents': 'documents',
    '/calendar':  'calendar',
    '/reports':   'reports',
  };

  const isFeatureLocked = (path) => {
    // Only the platform superadmin (WordPress administrator) bypasses plan restrictions.
    // Account admins (gpf_administrator) still respect their own account's plan limits.
    if (isPlatformAdmin || role === 'administrator') return false;
    const feat = FEATURE_MAP[path];
    if (!feat || !planFeatures) return false;
    const plan = userPlan || 'free';
    return planFeatures[plan]?.[feat] === false;
  };

  const handleNav = (path) => {
    if (!planLoaded && !isPlatformAdmin && role !== 'administrator') return;
    if (isFeatureLocked(path)) {
      const feat = FEATURE_MAP[path];
      setLockedFeature(feat);
      setShowLockModal(true);
      return;
    }
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const filteredNav = NAV_ITEMS.filter(item => {
    if (item.path === '/users')     return can('users', 'access');
    if (item.path === '/reports')   return can('reports', 'access');
    if (item.path === '/calendar')  return can('calendar', 'access');
    if (item.path === '/contacts')  return can('contacts', 'access');
    if (item.path === '/workspace')      return ADMIN_ROLES.includes(role) || can('users', 'access');
    if (item.path === '/import')         return ADMIN_ROLES.includes(role) || can('projects', 'add');
    if (item.path === '/platform-admin') return isPlatformAdmin || role === 'administrator';
    return true;
  });

  const sidebarWidth = isMobile ? 240 : (collapsed ? 64 : 220);

  const renderLogo = () => {
    if (collapsed && !isMobile) {
      return workspace?.company_logo
        ? <img src={workspace.company_logo} alt="logo" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'contain' }} />
        : <span style={styles.logoIcon}>G</span>;
    }
    if (workspace?.company_name) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
          {workspace.company_logo && (
            <img src={workspace.company_logo} alt="logo" style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'contain', flexShrink: 0 }} />
          )}
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {workspace.company_name}
            </div>
            <div style={{ fontSize: 10, color: '#60a5fa', marginTop: 1 }}>GoProjectFlow</div>
          </div>
        </div>
      );
    }
    return (
      <span style={styles.logoFull}>
        <span style={{ color: '#60a5fa' }}>Go</span>
        <span style={{ color: '#fff' }}>Project</span>
        <span style={{ color: '#fb923c' }}>Flow</span>
      </span>
    );
  };

  return (
    <div style={styles.shell}>
      {isMobile && (
        <button style={styles.hamburger} onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? '✕' : '☰'}
        </button>
      )}

      {isMobile && mobileOpen && (
        <div style={styles.backdrop} onClick={() => setMobileOpen(false)} />
      )}

      <aside style={{
        ...styles.sidebar,
        width: sidebarWidth,
        transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
        transition: isMobile ? 'transform 0.25s ease' : 'width 0.2s ease',
      }}>
        <div style={styles.logoArea} onClick={() => !isMobile && setCollapsed(!collapsed)}>
          {renderLogo()}
        </div>

        <nav style={styles.nav}>
          {filteredNav.map((item) => {
            const active  = location.pathname === item.path;
            const locked  = isFeatureLocked(item.path);
            // Only show the locked *appearance* once we've confirmed with a fresh fetch —
            // avoids a brief flash of an outdated lock badge right after a plan change.
            const showLocked = locked && planLoaded;
            return (
              <button
                key={item.path}
                onClick={() => handleNav(item.path)}
                style={{
                  ...styles.navItem,
                  background:     active ? '#1e3a5f' : 'transparent',
                  color:          showLocked ? '#475569' : active ? '#60a5fa' : '#94a3b8',
                  justifyContent: 'flex-start',
                  opacity:        showLocked ? 0.6 : 1,
                }}
              >
                <span style={styles.navIcon}><item.Icon /></span>
                {(!collapsed || isMobile) && (
                  <span style={{ ...styles.navLabel, flex: 1 }}>{item.label}</span>
                )}
                {(!collapsed || isMobile) && showLocked && (
                  <IconLock width={11} height={11} style={{ marginLeft: 4, opacity: 0.8 }} />
                )}
              </button>
            );
          })}
        </nav>

        <div style={styles.userArea}>
          <div style={styles.userRow} onClick={() => { navigate('/profile'); if (isMobile) setMobileOpen(false); }} title="Edit profile">
            {user?.avatar ? (
              <img src={user.avatar} alt={user.name} style={styles.userAvatar} />
            ) : (
              <div style={styles.userAvatarFallback}>
                {(user?.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
            )}
            {(!collapsed || isMobile) && <div style={styles.userName}>{user?.name || 'User'}</div>}
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn}>
            <IconLogout width={16} height={16} />
            {(!collapsed || isMobile) && <span style={{ marginLeft: 8 }}>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Feature Lock Modal */}
      {showLockModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 36, maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ marginBottom: 12, color: '#94a3b8', display: 'flex', justifyContent: 'center' }}><IconLock width={44} height={44} strokeWidth={1.4} /></div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>Feature Not Available</h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 8px' }}>
              <strong style={{ textTransform: 'capitalize' }}>{lockedFeature?.replace('_', ' ')}</strong> is not included in your current plan.
            </p>
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 24px' }}>Upgrade to unlock this feature and much more.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                style={{ padding: '12px 24px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                onClick={() => { setShowLockModal(false); navigate('/workspace?upgrade=1'); }}
              >Upgrade Plan →</button>
              <button
                style={{ padding: '11px 24px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}
                onClick={() => setShowLockModal(false)}
              >Maybe Later</button>
            </div>
          </div>
        </div>
      )}

      <main style={{
        ...styles.main,
        marginLeft: isMobile ? 0 : (collapsed ? 64 : 220),
        paddingTop: isMobile ? 56 : 32,
      }}>
        {children}
      </main>
    </div>
  );
}

const styles = {
  shell:             { display: 'flex', minHeight: '100dvh', fontFamily: "'Segoe UI', system-ui, sans-serif", background: '#f1f5f9' },
  hamburger:         { position: 'fixed', top: 10, left: 12, zIndex: 200, background: '#0f172a', border: 'none', color: 'white', fontSize: 20, width: 40, height: 40, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  backdrop:          { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 },
  sidebar:           { background: '#0f172a', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, height: '100dvh', zIndex: 100, overflow: 'hidden' },
  logoArea:          { padding: '20px', cursor: 'pointer', borderBottom: '1px solid #1e293b', minHeight: 68, display: 'flex', alignItems: 'center' },
  logoIcon:          { color: '#60a5fa', fontSize: 22, fontWeight: 800, width: '100%', textAlign: 'center' },
  logoFull:          { fontSize: 18, fontWeight: 800, letterSpacing: '-0.3px', whiteSpace: 'nowrap' },
  nav:               { flex: 1, padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' },
  navItem:           { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px 10px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, width: '100%', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  navIcon:           { display: 'flex', alignItems: 'center', justifyContent: 'flex-start', minWidth: 20, flexShrink: 0 },
  navLabel:          { fontSize: 14, textAlign: 'left' },
  userArea:          { padding: '16px 12px', borderTop: '1px solid #1e293b', flexShrink: 0 },
  userRow:           { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 8, cursor: 'pointer', marginBottom: 8 },
  userAvatar:        { width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  userAvatarFallback:{ width: 28, height: 28, borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 },
  userName:          { color: '#e2e8f0', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  logoutBtn:         { display: 'flex', alignItems: 'center', width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', borderRadius: 8, color: '#94a3b8', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
  main:              { flex: 1, padding: '32px', minHeight: '100dvh', transition: 'margin-left 0.2s ease', maxWidth: '100%', overflowX: 'hidden' },
};